import React, { useState, useEffect, useCallback, memo, useRef } from "react";
import "./UsageLimitComponent.css";

// ==================== INDEPENDENT AMOUNT INPUT ====================
const IndependentAmountInput = memo(({ initialValue, onValueChange, disabled }) => {
  const [localValue, setLocalValue] = useState(initialValue || "");
  const isInternalChange = useRef(false);
  const timeoutRef = useRef(null);

  useEffect(() => {
    if (!isInternalChange.current) {
      setLocalValue(initialValue || "");
    }
    isInternalChange.current = false;
  }, [initialValue]);

  const handleChange = (e) => {
    let newValue = e.target.value;
    
    if (newValue === "") {
      setLocalValue("");
      isInternalChange.current = true;
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      onValueChange("");
      return;
    }
    
    if (/^\d*\.?\d{0,2}$/.test(newValue) && newValue.length <= 10) {
      setLocalValue(newValue);
      isInternalChange.current = true;
      
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      timeoutRef.current = setTimeout(() => {
        onValueChange(newValue);
      }, 300);
    }
  };

  const handleClear = () => {
    setLocalValue("");
    isInternalChange.current = true;
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    onValueChange("");
  };

  return (
    <div className="custom-amount-input-wrapper">
      <div className="custom-amount-input-container">
        <span className="custom-currency-symbol">₹</span>
        <input
          type="text"
          className="custom-amount-input"
          value={localValue}
          onChange={handleChange}
          onWheel={(e) => e.target.blur()}
          placeholder="Enter amount limit"
          disabled={disabled}
        />
        {localValue && (
          <button className="custom-clear-btn" onClick={handleClear}>
            ✕
          </button>
        )}
      </div>
      <div className="custom-input-hint">Maximum amount (in ₹) per period</div>
    </div>
  );
});

// ==================== INDEPENDENT TRANSACTION INPUT ====================
const IndependentTransactionInput = memo(({ initialValue, onValueChange, disabled }) => {
  const [localValue, setLocalValue] = useState(initialValue || "");
  const isInternalChange = useRef(false);
  const timeoutRef = useRef(null);

  useEffect(() => {
    if (!isInternalChange.current) {
      setLocalValue(initialValue || "");
    }
    isInternalChange.current = false;
  }, [initialValue]);

  const handleChange = (e) => {
    let newValue = e.target.value;
    
    if (newValue === "") {
      setLocalValue("");
      isInternalChange.current = true;
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      onValueChange("");
      return;
    }
    
    if (/^\d*$/.test(newValue) && newValue.length <= 6) {
      setLocalValue(newValue);
      isInternalChange.current = true;
      
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      timeoutRef.current = setTimeout(() => {
        onValueChange(newValue);
      }, 300);
    }
  };

  const handleClear = () => {
    setLocalValue("");
    isInternalChange.current = true;
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    onValueChange("");
  };

  return (
    <div className="custom-transaction-input-wrapper">
      <div className="custom-transaction-input-container">
        <input
          type="text"
          className="custom-transaction-input"
          value={localValue}
          onChange={handleChange}
          onWheel={(e) => e.target.blur()}
          placeholder="Enter transaction count"
          disabled={disabled}
        />
        {localValue && (
          <button className="custom-clear-btn" onClick={handleClear}>
            ✕
          </button>
        )}
      </div>
      <div className="custom-input-hint">Maximum number of transactions per period</div>
    </div>
  );
});

// ==================== DURATION NUMBER INPUT ====================
const DurationInput = memo(({ value, onChange, unit, disabled }) => {
  const [localValue, setLocalValue] = useState(value);
  const timeoutRef = useRef(null);

  useEffect(() => {
    setLocalValue(value);
  }, [value]);

  const handleChange = (e) => {
    let newValue = e.target.value;
    
    if (newValue === "") {
      setLocalValue("");
      return;
    }
    
    if (/^\d*$/.test(newValue)) {
      let numValue = parseInt(newValue, 10);
      if (!isNaN(numValue) && numValue > 0) {
        setLocalValue(numValue);
        
        if (timeoutRef.current) clearTimeout(timeoutRef.current);
        timeoutRef.current = setTimeout(() => {
          onChange(numValue);
        }, 300);
      } else if (newValue === "0") {
        setLocalValue(1);
        onChange(1);
      }
    }
  };

  const handleBlur = () => {
    let numValue = parseInt(localValue, 10);
    if (isNaN(numValue) || numValue <= 0) {
      setLocalValue(1);
      onChange(1);
    } else {
      onChange(numValue);
    }
  };

  return (
    <div className="custom-duration-wrapper">
      <input
        type="number"
        className="custom-duration-input"
        value={localValue}
        onChange={handleChange}
        onBlur={handleBlur}
        onWheel={(e) => e.target.blur()}
        onKeyDown={(e) => {
          if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
            e.preventDefault();
          }
        }}
        disabled={disabled}
        min="1"
      />
      <span className="custom-duration-unit">{unit}</span>
    </div>
  );
});

// ==================== FREQUENCY BUTTONS ====================
const FrequencyButtons = memo(({ value, onChange, options, disabled }) => {
  return (
    <div className="custom-frequency-buttons">
      {options.map((option) => (
        <button
          key={option.value}
          className={`custom-freq-btn ${value === option.value ? 'active' : ''}`}
          onClick={() => onChange(option.value)}
          disabled={disabled}
        >
          {option.label}
        </button>
      ))}
    </div>
  );
});

// ==================== AMOUNT SECTION ====================
const AmountSection = memo(({ 
  initialValue,
  initialFrequency,
  initialDuration,
  onDataChange,
  loading,
  title,
  color
}) => {
  const [value, setValue] = useState(initialValue || "");
  const [frequency, setFrequency] = useState(initialFrequency || "DAYS");
  const [duration, setDuration] = useState(initialDuration || 1);
  
  const isInitialized = useRef(false);
  const timeoutRef = useRef(null);

  const frequencyOptions = [
    { value: "HOURS", label: "Hour" },
    { value: "DAYS", label: "Day" },
    { value: "MONTHS", label: "Month" },
  ];

  const getUnit = (freq) => {
    switch(freq) {
      case "HOURS": return "hrs";
      case "DAYS": return "days";
      case "MONTHS": return "months";
      default: return "days";
    }
  };

  const unit = getUnit(frequency);

  // Stabilized callback - use ref to avoid re-renders
  const stableOnDataChange = useRef(onDataChange);
  useEffect(() => {
    stableOnDataChange.current = onDataChange;
  }, [onDataChange]);

  const sendDataToParent = useCallback((newValue, newFrequency, newDuration) => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => {
      if (newValue && parseFloat(newValue) > 0) {
        stableOnDataChange.current({
          limitOn: "AMOUNT",
          value: parseFloat(newValue),
          frequency: newFrequency,
          duration: newDuration
        });
      }
    }, 300);
  }, []);

  const handleValueChange = useCallback((newValue) => {
    setValue(newValue);
    sendDataToParent(newValue, frequency, duration);
  }, [frequency, duration, sendDataToParent]);

  const handleFrequencyChange = useCallback((newFrequency) => {
    setFrequency(newFrequency);
    setDuration(1);
    sendDataToParent(value, newFrequency, 1);
  }, [value, sendDataToParent]);

  const handleDurationChange = useCallback((newDuration) => {
    setDuration(newDuration);
    sendDataToParent(value, frequency, newDuration);
  }, [value, frequency, sendDataToParent]);

  useEffect(() => {
    if (!isInitialized.current && initialValue) {
      setValue(initialValue);
      let uiFrequency = initialFrequency;
      if (uiFrequency === "MONTH") uiFrequency = "MONTHS";
      setFrequency(uiFrequency || "DAYS");
      setDuration(initialDuration || 1);
      isInitialized.current = true;
    }
  }, [initialValue, initialFrequency, initialDuration]);

  return (
    <div className="custom-limit-section" style={{ marginBottom: '24px' }}>
      <h4 className="custom-section-title" style={{ color: color || '#2e7d32' }}>
        {title || "Amount Limit"}
      </h4>

      <div className="custom-field-group">
        <div className="custom-field-header">
          <span className="custom-field-label">Limit Value</span>
          <span className="custom-field-counter">{value.length}/10</span>
        </div>
        <IndependentAmountInput
          initialValue={value}
          onValueChange={handleValueChange}
          disabled={loading}
        />
      </div>

      <div className="custom-field-group">
        <span className="custom-field-label">Frequency</span>
        <FrequencyButtons
          value={frequency}
          onChange={handleFrequencyChange}
          options={frequencyOptions}
          disabled={loading}
        />
      </div>

      <div className="custom-field-group">
        <span className="custom-field-label">Duration</span>
        <DurationInput
          value={duration}
          onChange={handleDurationChange}
          disabled={loading}
          unit={unit}
        />
      </div>
    </div>
  );
});

// ==================== TRANSACTION SECTION ====================
const TransactionSection = memo(({ 
  initialValue,
  initialFrequency,
  initialDuration,
  onDataChange,
  loading,
  title,
  color
}) => {
  const [value, setValue] = useState(initialValue || "");
  const [frequency, setFrequency] = useState(initialFrequency || "DAYS");
  const [duration, setDuration] = useState(initialDuration || 1);
  
  const isInitialized = useRef(false);
  const timeoutRef = useRef(null);

  const frequencyOptions = [
    { value: "HOURS", label: "Hour" },
    { value: "DAYS", label: "Day" },
    { value: "MONTHS", label: "Month" },
  ];

  const getUnit = (freq) => {
    switch(freq) {
      case "HOURS": return "hrs";
      case "DAYS": return "days";
      case "MONTHS": return "months";
      default: return "days";
    }
  };

  const unit = getUnit(frequency);

  const stableOnDataChange = useRef(onDataChange);
  useEffect(() => {
    stableOnDataChange.current = onDataChange;
  }, [onDataChange]);

  const sendDataToParent = useCallback((newValue, newFrequency, newDuration) => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => {
      if (newValue && parseFloat(newValue) > 0) {
        stableOnDataChange.current({
          limitOn: "TRANSACTION",
          value: parseInt(newValue, 10),
          frequency: newFrequency,
          duration: newDuration
        });
      }
    }, 300);
  }, []);

  const handleValueChange = useCallback((newValue) => {
    setValue(newValue);
    sendDataToParent(newValue, frequency, duration);
  }, [frequency, duration, sendDataToParent]);

  const handleFrequencyChange = useCallback((newFrequency) => {
    setFrequency(newFrequency);
    setDuration(1);
    sendDataToParent(value, newFrequency, 1);
  }, [value, sendDataToParent]);

  const handleDurationChange = useCallback((newDuration) => {
    setDuration(newDuration);
    sendDataToParent(value, frequency, newDuration);
  }, [value, frequency, sendDataToParent]);

  useEffect(() => {
    if (!isInitialized.current && initialValue) {
      setValue(initialValue);
      let uiFrequency = initialFrequency;
      if (uiFrequency === "MONTH") uiFrequency = "MONTHS";
      setFrequency(uiFrequency || "DAYS");
      setDuration(initialDuration || 1);
      isInitialized.current = true;
    }
  }, [initialValue, initialFrequency, initialDuration]);

  return (
    <div className="custom-limit-section" style={{ marginBottom: '16px' }}>
      <h4 className="custom-section-title" style={{ color: color || '#1565c0' }}>
        {title || "Transaction Limit"}
      </h4>

      <div className="custom-field-group">
        <div className="custom-field-header">
          <span className="custom-field-label">Transaction Count</span>
          <span className="custom-field-counter">{value.length}/6</span>
        </div>
        <IndependentTransactionInput
          initialValue={value}
          onValueChange={handleValueChange}
          disabled={loading}
        />
      </div>

      <div className="custom-field-group">
        <span className="custom-field-label">Frequency</span>
        <FrequencyButtons
          value={frequency}
          onChange={handleFrequencyChange}
          options={frequencyOptions}
          disabled={loading}
        />
      </div>

      <div className="custom-field-group">
        <span className="custom-field-label">Duration</span>
        <DurationInput
          value={duration}
          onChange={handleDurationChange}
          disabled={loading}
          unit={unit}
        />
      </div>
    </div>
  );
});

// ==================== MAIN COMPONENT ====================
const UsageLimitComponent = ({
  usageLimitRequired,
  handleUsageLimitChange,
  onLimitsChange,
  initialLimits = {},
  loading,
}) => {
  const [enableBP, setEnableBP] = useState(false);
  const [enableTagPay, setEnableTagPay] = useState(false);
  const [bpAmount, setBpAmount] = useState(null);
  const [bpTransaction, setBpTransaction] = useState(null);
  const [tagAmount, setTagAmount] = useState(null);
  const [tagTransaction, setTagTransaction] = useState(null);
  
  // Use ref to track if initial load is done
  const isInitialLoadDone = useRef(false);

  // Initialize from existing data - runs only once on mount
  useEffect(() => {
    if (!isInitialLoadDone.current && initialLimits && Object.keys(initialLimits).length > 0) {
      const accountTypes = initialLimits.accountTypes || [];
      setEnableBP(accountTypes.includes("WALLET"));
      setEnableTagPay(accountTypes.includes("WALLET_RFID"));
      
      if (initialLimits.bpLimits) {
        const amountLimit = initialLimits.bpLimits.find(l => l.limitOn === "AMOUNT");
        const transactionLimit = initialLimits.bpLimits.find(l => l.limitOn === "TRANSACTION");
        if (amountLimit) setBpAmount(amountLimit);
        if (transactionLimit) setBpTransaction(transactionLimit);
      }
      
      if (initialLimits.tagLimits) {
        const amountLimit = initialLimits.tagLimits.find(l => l.limitOn === "AMOUNT");
        const transactionLimit = initialLimits.tagLimits.find(l => l.limitOn === "TRANSACTION");
        if (amountLimit) setTagAmount(amountLimit);
        if (transactionLimit) setTagTransaction(transactionLimit);
      }
      isInitialLoadDone.current = true;
    }
  }, [initialLimits]); // Only runs when initialLimits changes

  // Send data to parent - only when user interacts
  const sendCombinedData = useCallback(() => {
    const accountTypes = [];
    const bpLimits = [];
    const tagLimits = [];
    
    if (enableBP) {
      accountTypes.push("WALLET");
      if (bpAmount && bpAmount.value && bpAmount.value > 0) {
        bpLimits.push(bpAmount);
      }
      if (bpTransaction && bpTransaction.value && bpTransaction.value > 0) {
        bpLimits.push(bpTransaction);
      }
    }
    
    if (enableTagPay) {
      accountTypes.push("WALLET_RFID");
      if (tagAmount && tagAmount.value && tagAmount.value > 0) {
        tagLimits.push(tagAmount);
      }
      if (tagTransaction && tagTransaction.value && tagTransaction.value > 0) {
        tagLimits.push(tagTransaction);
      }
    }
    
    const usageLimitData = {
      usageLimitRequired: usageLimitRequired === "Yes",
      accountTypes: accountTypes,
    };
    
    if (bpLimits.length > 0) {
      usageLimitData.bpLimits = bpLimits;
    }
    
    if (tagLimits.length > 0) {
      usageLimitData.tagLimits = tagLimits;
    }
    
    if (onLimitsChange && usageLimitRequired === "Yes") {
      onLimitsChange(usageLimitData);
    } else if (usageLimitRequired === "No") {
      onLimitsChange(null);
    }
  }, [enableBP, enableTagPay, bpAmount, bpTransaction, tagAmount, tagTransaction, usageLimitRequired, onLimitsChange]);

  // Only trigger when user interacts (checkbox or limit changes)
  const handleBPChange = (e) => {
    const isChecked = e.target.checked;
    setEnableBP(isChecked);
    if (!isChecked) {
      setBpAmount(null);
      setBpTransaction(null);
    }
    // Send data after state update
    setTimeout(() => sendCombinedData(), 0);
  };

  const handleTagPayChange = (e) => {
    const isChecked = e.target.checked;
    setEnableTagPay(isChecked);
    if (!isChecked) {
      setTagAmount(null);
      setTagTransaction(null);
    }
    setTimeout(() => sendCombinedData(), 0);
  };

  // Watch for limit changes
  useEffect(() => {
    if (isInitialLoadDone.current || enableBP || enableTagPay) {
      sendCombinedData();
    }
  }, [bpAmount, bpTransaction, tagAmount, tagTransaction, enableBP, enableTagPay]);

  return (
    <div className="custom-usage-limit-container">
      <h3 className="custom-main-title">Usage Limit</h3>
      <p className="custom-main-subtitle">
        Define spending thresholds and transaction volume controls for this corporate account.
      </p>

      <div className="custom-radio-card">
        <span className="custom-radio-label">Do you want to set a usage limit?</span>
        
        <div className="custom-radio-group">
          <label className={`custom-radio-option ${usageLimitRequired === "Yes" ? 'active' : ''}`}>
            <input
              type="radio"
              name="usageLimit"
              value="Yes"
              checked={usageLimitRequired === "Yes"}
              onChange={(e) => handleUsageLimitChange(e.target.value)}
            />
            <div className="custom-radio-content">
              <strong>Yes</strong>
              <small>Set a usage limit for corporate</small>
            </div>
          </label>
          
          <label className={`custom-radio-option ${usageLimitRequired === "No" ? 'active' : ''}`}>
            <input
              type="radio"
              name="usageLimit"
              value="No"
              checked={usageLimitRequired === "No"}
              onChange={(e) => handleUsageLimitChange(e.target.value)}
            />
            <div className="custom-radio-content">
              <strong>No</strong>
              <small>No usage limit required</small>
            </div>
          </label>
        </div>
      </div>

      {usageLimitRequired === "Yes" && (
        <div className="custom-limits-card">
          <span className="custom-account-label">Select Account Type</span>
          
          <div className="custom-checkbox-group">
            <label className="custom-checkbox-option">
              <input
                type="checkbox"
                checked={enableBP}
                onChange={handleBPChange}
              />
              <span>Business Payment (BP)</span>
            </label>
            <label className="custom-checkbox-option">
              <input
                type="checkbox"
                checked={enableTagPay}
                onChange={handleTagPayChange}
              />
              <span>TagPay</span>
            </label>
          </div>

          {/* BP Limits */}
          {enableBP && (
            <>
              <AmountSection
                initialValue={bpAmount?.value?.toString() || ""}
                initialFrequency={bpAmount?.frequency || "DAYS"}
                initialDuration={bpAmount?.duration || 1}
                onDataChange={setBpAmount}
                loading={loading}
                title="BP Amount Limit"
                color="#2e7d32"
              />
              <TransactionSection
                initialValue={bpTransaction?.value?.toString() || ""}
                initialFrequency={bpTransaction?.frequency || "DAYS"}
                initialDuration={bpTransaction?.duration || 1}
                onDataChange={setBpTransaction}
                loading={loading}
                title="BP Transaction Limit"
                color="#1565c0"
              />
            </>
          )}

          {/* TagPay Limits */}
          {enableTagPay && (
            <>
              <AmountSection
                initialValue={tagAmount?.value?.toString() || ""}
                initialFrequency={tagAmount?.frequency || "DAYS"}
                initialDuration={tagAmount?.duration || 1}
                onDataChange={setTagAmount}
                loading={loading}
                title="TagPay Amount Limit"
                color="#2e7d32"
              />
              <TransactionSection
                initialValue={tagTransaction?.value?.toString() || ""}
                initialFrequency={tagTransaction?.frequency || "DAYS"}
                initialDuration={tagTransaction?.duration || 1}
                onDataChange={setTagTransaction}
                loading={loading}
                title="TagPay Transaction Limit"
                color="#1565c0"
              />
            </>
          )}

          {!enableBP && !enableTagPay && (
            <div className="custom-empty-state">
              Please select at least one account type
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default UsageLimitComponent;